// @flow

import * as React from 'react';
import ApiClient from 'speed-js-core/src/api/ApiClient';
import { get } from 'lodash';
import { Confirmation, connectAPI, withConfirmation } from 'speed-js-react';
import type { UUID } from 'esm-web-pro/types/common';
import { defineMessages, injectIntl, IntlShape } from 'react-intl';
import TimelineIcon from 'esm-web-pro/components/layout/sidePanel/icons/TimelineIcon';
import actionMessages from 'esm-web-pro/messages/actionMessages';
import DeleteIcon from 'esm-web-pro/components/layout/sidePanel/icons/DeleteIcon';
import { withRouter } from 'react-router-dom';
import type { RouterHistory, Match } from 'react-router-dom';
import BaseEditor from 'esm-web-pro/containers/map/editor/BaseEditor';
import MainActions from 'esm-web-pro/components/layout/sidePanel/MainActions';
import EditIcon from 'esm-web-pro/components/layout/sidePanel/icons/EditIcon';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import * as sidePanelActions from 'esm-web-pro/redux/side-panel/actions';
import SidePane from 'esm-web-pro/components/layout/SidePane';
import PipeEditor from 'esm-web-pro/containers/pages/network/objects/pipe/PipeEditor';
import ContinuousPipeEditor from 'esm-web-pro/containers/pages/network/objects/pipe/ContinuousPipeEditor';


type SidePanelActionProps = {
    openPanel: (key: string, url: ?string, sensitivity?: boolean) => { key: string, url: string },
    closePanel: (key: string, skipSensitivity: boolean) => { key: string },
}

type Props = {
    ...$Exact<SidePanelActionProps>,
    continuousPipe: Object,
    map: Object,
    confirm: (confirmation: Confirmation) => void,
    intl: IntlShape,
    deleteObject: (id: UUID, resourceName: string, resourceArguments: Array<Object>) => Promise<Object>,
    history: RouterHistory,
    match: Match,
    editor: BaseEditor,
    onEdit: Function,
}

const msg = defineMessages({
    deleteTitle: {
        id: 'delete-continuous-pipe-title',
        defaultMessage: 'Are you sure you want to delete the continuous pipe? '
            + 'This action will delete also all its segments!',
    },
    cancelDelete: {
        id: 'delete-pipe-cancel',
        defaultMessage: 'Cancel',
    },
    confirmDelete: {
        id: 'delete-pipe-confirm',
        defaultMessage: 'Confirm',
    },
    cantDelete: {
        id: 'cant-delete-pipe',
        defaultMessage: 'The continuous pipe cannot be deleted because its segments contain cables.',
    },
    title: {
        id: 'continuous-pipe-editor-title',
        defaultMessage: 'Edit continuous pipe',
    },
});

const CONTINUOUS_PIPE_EDIT_KEY = 'CONTINUOUS_PIPE_EDIT_KEY ';

const ContinuousPipeActions = (props: Props) => {
    const {
        continuousPipe, map: { is_draft: isDraft, identity: mapIdentity }, confirm, intl: { formatMessage },
        deleteObject, history, match, editor, openPanel, closePanel, onEdit,
    } = props;

    const handleClose = (skipSensitivity = false) => {
        closePanel(CONTINUOUS_PIPE_EDIT_KEY, skipSensitivity);
    };

    const handleSave = (newValues) => {
        onEdit(newValues);
        handleClose(true);
    };


    const disallowDeleteConfirm = (message) => confirm({
        message,
        actions: {
            cancel: {
                label: formatMessage(msg.cancelDelete),
                callback: () => {
                },
                type: 'negative',
            },
        },
    });

    const allowDeleteConfirm = () => confirm({
        message: formatMessage(msg.deleteTitle),
        actions: {
            cancel: {
                label: formatMessage(msg.cancelDelete),
                callback: () => {},
                type: 'negative',
            },
            confirm: {
                label: formatMessage(msg.confirmDelete),
                callback: () => {
                    if (deleteObject) {
                        deleteObject(continuousPipe.uuid, 'continuousPipe', []).then((res) => {
                            if (res.data && res.data.error) {
                                disallowDeleteConfirm(res.data.error);
                            } else {
                                history.push(match.url.split('/layer')[0]);
                            }
                        });
                    }
                },
                type: 'positive',
            },
        },
    });

    const deletePipe = () => {
        if (continuousPipe.pipes.some((p) => p.cables.length > 0)) {
            disallowDeleteConfirm(formatMessage(msg.cantDelete));
        } else {
            allowDeleteConfirm();
        }
    };

    const actions = [];

    if (isDraft) {
        actions.push(
            {
                key: `${continuousPipe.uuid}-edit`,
                icon: <EditIcon />,
                callback: () => openPanel(
                    CONTINUOUS_PIPE_EDIT_KEY,
                    '',
                    match.url,
                    true,
                ),
                tooltip: formatMessage(actionMessages.edit),
                disabled: editor.state.loading,
                marginVertical: '15px',
                radius: '0%',
                color: 'transparent',
            },
        );
    }


    actions.push({
        key: `${continuousPipe.uuid}-timeline`,
        icon: <TimelineIcon />,
        callback: () => {
            history.push(`${match.url}/timeline/continuouspipe`.replace('//', '/'));
        },
        tooltip: formatMessage(actionMessages.timeline),
        marginVertical: '15px',
        radius: '0%',
        color: 'transparent',
    });

    if (isDraft) {
        actions.push({
            key: `${continuousPipe.uuid}-delete`,
            icon: <DeleteIcon />,
            callback: () => deletePipe(),
            tooltip: formatMessage(actionMessages.delete),
            disabled: editor.state.loading,
            marginVertical: '15px',
            radius: '0%',
            color: 'transparent',
        });
    }

    return (
        <>
            <SidePane
                id={CONTINUOUS_PIPE_EDIT_KEY}
                key={continuousPipe.uuid}
                title={`${formatMessage(msg.title)} ${continuousPipe.ref}`}
            >
                <ContinuousPipeEditor
                    id={CONTINUOUS_PIPE_EDIT_KEY}
                    continuousPipe={continuousPipe}
                    onClose={handleClose}
                    onSave={handleSave}
                    mapIdentity={mapIdentity}
                    history={history}
                    match={match}
                />
            </SidePane>
            <MainActions main actions={actions} />
        </>
    );
};

const apiCallback = (apiClient: ApiClient) => ({
    deleteObject: {
        send: (id, resourceName, resourceArguments = []) => {
            const resource = get(apiClient.resources, resourceName);
            return resource.delete(...resourceArguments, id);
        },
    },
});

const mapDispatchToProps = (dispatch) => (
    bindActionCreators(sidePanelActions, dispatch)
);


export default withConfirmation(injectIntl(withRouter(connectAPI(apiCallback)(
    connect(null, mapDispatchToProps)(ContinuousPipeActions),
))));
